import { useState, useCallback } from 'react';
import { Check, Droplets, FlaskConical } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { householdCompounds, householdCompoundColors } from '@/config/experiments/chem1131/experiment1.config';

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Testing Soluble Compounds for pH',
    content: `The 7 soluble compounds now need to be distinguished. pH testing with phenolphthalein separates basic from neutral/acidic solutions.

**The Seven Soluble Compounds:**
| Formula | Common Name | Expected pH |
|---------|-------------|-------------|
| NaCl | Table salt | ~7 (neutral) |
| NaHCO₃ | Baking soda | ~8.3 (weakly basic) |
| Na₂CO₃ | Washing soda | ~11.5 (basic) |
| NaOH | Drano | ~14 (strongly basic) |
| MgSO₄ | Epsom salt | ~7 (neutral) |
| H₃BO₃ | Boric acid | ~5 (weakly acidic) |
| Sucrose | Table sugar | ~7 (neutral) |

Phenolphthalein will identify the strongly basic compounds (Na₂CO₃ and NaOH).`
  },
  2: {
    title: 'How Phenolphthalein Works',
    content: `Phenolphthalein is a pH indicator that changes color at pH ~8.2-10.

**The Color Change:**
- **pH < 8.2**: Colorless (protonated form)
- **pH > 10**: Pink/Magenta (deprotonated form)

**Chemical Structure Change:**
The indicator molecule exists in different forms:
- In acid: Lactone form (colorless)
- In base: Quinoid form (pink)

**Why Only Strong Bases Turn Pink:**
- NaHCO₃ (pH ~8.3) is right at the transition - stays colorless or very faint pink
- Na₂CO₃ (pH ~11.5) and NaOH (pH ~14) turn vivid pink

**Transition Range:**
The color change isn't instant - it occurs over pH 8.2 to 10. Only solutions well above this range give strong pink color.`
  },
  3: {
    title: 'Interpreting pH Indicator Results',
    content: `The phenolphthalein test divides soluble compounds into two groups.

**Results Summary:**
| Color | pH Range | Compounds |
|-------|----------|-----------|
| Vivid Pink | >10 | Na₂CO₃, NaOH |
| Colorless | <8.2 | NaCl, MgSO₄, Sucrose, NaHCO₃, H₃BO₃ |

**Why These Results?**
- **Na₂CO₃**: CO₃²⁻ hydrolyzes: CO₃²⁻ + H₂O → HCO₃⁻ + OH⁻
- **NaOH**: Direct source of OH⁻ ions
- **Neutral salts**: No hydrolysis, pH stays ~7
- **NaHCO₃**: Weak base, pH only ~8.3 (below threshold)
- **H₃BO₃**: Weak acid, actually lowers pH

**Next Steps:**
The 2 basic compounds need vinegar test to distinguish them.
The 5 neutral/acidic compounds need additional tests.`
  }
};

type PhenoResult = 'none' | 'pink' | 'colorless';

export default function Part5Phenolphthalein() {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [solubleSelected, setSolubleSelected] = useState(false);
  const [phenoAdded, setPhenoAdded] = useState(false);
  const [resultsRecorded, setResultsRecorded] = useState(false);
  const [phenoResults, setPhenoResults] = useState<Record<string, PhenoResult>>({});
  const [currentStep, setCurrentStep] = useState(1);

  // Only soluble compounds
  const solubleCompounds = Object.values(householdCompounds).filter(c => c.soluble);
  const completedSteps = [solubleSelected, phenoAdded, resultsRecorded].filter(Boolean).length;

  const handleSelectSoluble = useCallback(() => {
    if (!solubleSelected) {
      setSolubleSelected(true);
      setCurrentStep(2);
    }
  }, [solubleSelected]);

  const handleAddPheno = useCallback((compoundId: string) => {
    if (solubleSelected && !phenoAdded) {
      setPhenoResults(prev => {
        const compound = householdCompounds[compoundId];
        const result: PhenoResult = compound.basicWithPhenolphthalein ? 'pink' : 'colorless';
        const updated = { ...prev, [compoundId]: result };

        if (Object.keys(updated).length === solubleCompounds.length) {
          setPhenoAdded(true);
          setCurrentStep(3);
        }
        return updated;
      });
    }
  }, [solubleSelected, phenoAdded, solubleCompounds.length]);

  const handleRecordResults = useCallback(() => {
    if (phenoAdded && !resultsRecorded) {
      setResultsRecorded(true);
    }
  }, [phenoAdded, resultsRecorded]);

  const getSolutionColor = (compoundId: string, result: PhenoResult) => {
    if (result === 'none') return householdCompoundColors.dissolved;
    if (result === 'pink') return householdCompoundColors.phenolphthaleinPink;
    return householdCompoundColors.dissolved;
  };

  const basicCompounds = solubleCompounds.filter(c => c.basicWithPhenolphthalein);
  const neutralCompounds = solubleCompounds.filter(c => !c.basicWithPhenolphthalein);

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part V: Phenolphthalein Test</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Select Soluble', hint: 'Identify the 7 soluble solutions', done: solubleSelected },
            { id: 2, title: 'Add Indicator', hint: 'Add 1-2 drops to each', done: phenoAdded },
            { id: 3, title: 'Record Results', hint: 'Note pink vs colorless', done: resultsRecorded }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* pH Indicator Info */}
        <div className="indicator-panel">
          <h4>Phenolphthalein Indicator</h4>
          <div className="ph-scale">
            <div className="scale-bar">
              <div className="acidic"></div>
              <div className="neutral"></div>
              <div className="basic"></div>
            </div>
            <div className="scale-labels">
              <span>Acidic</span>
              <span>pH 8.2</span>
              <span>Basic</span>
            </div>
            <div className="color-labels">
              <span className="colorless-label">Colorless</span>
              <span className="pink-label">Pink/Magenta</span>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        {phenoAdded && (
          <div className="results-summary">
            <div className="result-group">
              <span className="result-label pink">Basic (Pink):</span>
              <div className="compound-tags">
                {basicCompounds.map(c => (
                  <span key={c.id} className="tag pink">{c.formula}</span>
                ))}
              </div>
            </div>
            <div className="result-group">
              <span className="result-label colorless">Neutral (Colorless):</span>
              <div className="compound-tags">
                {neutralCompounds.map(c => (
                  <span key={c.id} className="tag colorless">{c.formula}</span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <LabBench
        experimentName="pH Indicator Test"
        completedCount={completedSteps}
        totalSteps={3}
        equipment={['Phenolphthalein indicator', 'Dropper', 'pH chart']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="pheno-workspace">
          {/* Indicator Bottle */}
          <div className="reagent-area">
            <div className="indicator-bottle">
              <svg viewBox="0 0 40 80" width={50} height={90}>
                <rect x="8" y="20" width="24" height="50" rx="2" fill="#4A4A4A" />
                <rect x="10" y="25" width="20" height="40" fill="#FFB6C1" opacity="0.6" />
                <rect x="12" y="8" width="16" height="15" rx="2" fill="#333" />
                <circle cx="20" cy="68" r="2" fill="#FFB6C1" />
              </svg>
              <span className="bottle-label">Phenolphthalein</span>
            </div>
          </div>

          {/* Selection Prompt */}
          {!solubleSelected && (
            <div className="selection-prompt" onClick={handleSelectSoluble}>
              <FlaskConical size={32} className="prompt-icon" />
              <h3>Test Soluble Compounds</h3>
              <p>The 7 compounds that dissolved in water:</p>
              <div className="compound-grid">
                {solubleCompounds.map(c => (
                  <span key={c.id} className="compound-chip">{c.formula}</span>
                ))}
              </div>
              <button className="select-btn">Begin pH Testing</button>
            </div>
          )}

          {/* Test Tubes Grid */}
          {solubleSelected && (
            <div className="tubes-section">
              <div className="tubes-grid">
                {solubleCompounds.map((compound) => {
                  const result = phenoResults[compound.id] || 'none';
                  const isBasic = compound.basicWithPhenolphthalein;

                  return (
                    <div
                      key={compound.id}
                      className={`tube-card ${currentStep === 2 && result === 'none' ? 'clickable' : ''}`}
                      onClick={() => {
                        if (currentStep === 2 && result === 'none') {
                          handleAddPheno(compound.id);
                        }
                      }}
                    >
                      <svg viewBox="0 0 40 90" width={45} height={100}>
                        {/* Test tube */}
                        <rect x="8" y="8" width="24" height="60" rx="2" fill="rgba(200,220,240,0.3)" stroke="#7A9BB8" strokeWidth="1.5" />
                        <ellipse cx="20" cy="68" rx="12" ry="5" fill="rgba(200,220,240,0.4)" stroke="#7A9BB8" strokeWidth="1" />

                        {/* Solution */}
                        <rect
                          x="10"
                          y="25"
                          width="20"
                          height="40"
                          fill={getSolutionColor(compound.id, result)}
                          rx="1"
                          opacity="0.9"
                        />

                        {/* Pink glow for basic */}
                        {result === 'pink' && (
                          <rect x="10" y="25" width="20" height="40" fill="#E91E63" opacity="0.3" rx="1">
                            <animate attributeName="opacity" values="0.3;0.5;0.3" dur="2s" repeatCount="indefinite" />
                          </rect>
                        )}

                        {/* Label */}
                        <rect x="10" y="10" width="20" height="10" fill="#FFFACD" stroke="#DAA520" strokeWidth="0.3" rx="1" />
                      </svg>

                      <span className="tube-formula">{compound.formula}</span>
                      <span className="tube-name">{compound.commonName}</span>

                      {result !== 'none' && (
                        <span className={`result-badge ${result}`}>
                          {result === 'pink' ? 'BASIC' : 'Neutral'}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {currentStep === 2 && !phenoAdded && (
                <div className="progress-indicator">
                  <Droplets size={16} />
                  <span>Click each tube to add indicator ({Object.keys(phenoResults).length}/{solubleCompounds.length})</span>
                </div>
              )}
            </div>
          )}

          {/* Status Messages */}
          <div className="status-area">
            {phenoAdded && !resultsRecorded && (
              <div className="status-message info" onClick={handleRecordResults} style={{ cursor: 'pointer' }}>
                <FlaskConical size={20} />
                Click to record: Na<sub>2</sub>CO<sub>3</sub> and NaOH turned pink (basic)
              </div>
            )}
            {resultsRecorded && (
              <div className="status-message success">
                <Check size={20} />
                pH test complete! 2 basic compounds identified (Na<sub>2</sub>CO<sub>3</sub>, NaOH).
              </div>
            )}
          </div>

          {/* Theory Box */}
          {resultsRecorded && (
            <div className="theory-box">
              <h4>pH Analysis Results</h4>
              <table className="results-table">
                <thead>
                  <tr>
                    <th>Compound</th>
                    <th>pH</th>
                    <th>Color</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="basic">
                    <td>Na<sub>2</sub>CO<sub>3</sub></td>
                    <td>~11.5</td>
                    <td>Vivid Pink</td>
                    <td>Strong base</td>
                  </tr>
                  <tr className="basic">
                    <td>NaOH</td>
                    <td>~14</td>
                    <td>Vivid Pink</td>
                    <td>Very strong base</td>
                  </tr>
                  <tr>
                    <td>NaHCO<sub>3</sub></td>
                    <td>~8.3</td>
                    <td>Colorless</td>
                    <td>Weakly basic (below threshold)</td>
                  </tr>
                  <tr>
                    <td>NaCl</td>
                    <td>~7</td>
                    <td>Colorless</td>
                    <td>Neutral salt</td>
                  </tr>
                  <tr>
                    <td>MgSO<sub>4</sub></td>
                    <td>~7</td>
                    <td>Colorless</td>
                    <td>Neutral salt</td>
                  </tr>
                  <tr>
                    <td>Sucrose</td>
                    <td>~7</td>
                    <td>Colorless</td>
                    <td>Neutral (covalent)</td>
                  </tr>
                  <tr>
                    <td>H<sub>3</sub>BO<sub>3</sub></td>
                    <td>~5</td>
                    <td>Colorless</td>
                    <td>Weak acid</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        <style>{`
          .pheno-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .reagent-area {
            display: flex;
            justify-content: center;
          }

          .indicator-bottle {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .bottle-label {
            font-size: 12px;
            color: var(--text-secondary);
          }

          .selection-prompt {
            background: var(--bg-elevated);
            border: 2px solid var(--border);
            border-radius: 12px;
            padding: 24px;
            text-align: center;
            max-width: 450px;
            cursor: pointer;
            transition: all 0.2s;
          }

          .selection-prompt:hover {
            border-color: var(--secondary);
          }

          .prompt-icon {
            color: var(--secondary);
            margin-bottom: 12px;
          }

          .selection-prompt h3 {
            margin: 0 0 8px 0;
          }

          .selection-prompt p {
            margin: 0 0 16px 0;
            color: var(--text-secondary);
            font-size: 14px;
          }

          .compound-grid {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            justify-content: center;
            margin-bottom: 16px;
          }

          .compound-chip {
            background: var(--bg-surface);
            padding: 6px 12px;
            border-radius: 15px;
            font-size: 12px;
            color: var(--text-primary);
          }

          .select-btn {
            background: var(--secondary);
            color: white;
            border: none;
            padding: 10px 24px;
            border-radius: 6px;
            font-weight: 600;
            cursor: pointer;
          }

          .tubes-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 16px;
          }

          .tubes-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 12px;
            background: linear-gradient(180deg, #5C534A 0%, #3D352F 100%);
            padding: 20px;
            border-radius: 10px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          }

          .tube-card {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
            padding: 8px;
            border-radius: 6px;
            transition: all 0.2s;
            background: rgba(255,255,255,0.02);
          }

          .tube-card.clickable {
            cursor: pointer;
          }

          .tube-card.clickable:hover {
            background: rgba(255,255,255,0.08);
          }

          .tube-formula {
            font-size: 10px;
            color: #EEE;
            font-weight: 600;
          }

          .tube-name {
            font-size: 8px;
            color: #999;
            text-align: center;
          }

          .result-badge {
            font-size: 9px;
            padding: 2px 6px;
            border-radius: 3px;
            font-weight: 600;
          }

          .result-badge.pink {
            background: #E91E63;
            color: white;
          }

          .result-badge.colorless {
            background: rgba(150, 150, 150, 0.3);
            color: #AAA;
          }

          .progress-indicator {
            display: flex;
            align-items: center;
            gap: 8px;
            color: #60A5FA;
            font-size: 14px;
          }

          .indicator-panel {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 12px;
            margin-top: 16px;
          }

          .indicator-panel h4 {
            margin: 0 0 12px 0;
            font-size: 13px;
            color: var(--secondary);
          }

          .ph-scale {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .scale-bar {
            display: flex;
            height: 12px;
            border-radius: 6px;
            overflow: hidden;
          }

          .scale-bar .acidic {
            flex: 4;
            background: linear-gradient(90deg, #FF6B6B, #FFE66D);
          }

          .scale-bar .neutral {
            flex: 1;
            background: #FFE66D;
          }

          .scale-bar .basic {
            flex: 3;
            background: linear-gradient(90deg, #FFE66D, #E91E63);
          }

          .scale-labels {
            display: flex;
            justify-content: space-between;
            font-size: 10px;
            color: var(--text-secondary);
          }

          .color-labels {
            display: flex;
            justify-content: space-between;
            font-size: 9px;
            margin-top: 4px;
          }

          .colorless-label {
            color: #999;
          }

          .pink-label {
            color: #E91E63;
          }

          .results-summary {
            background: var(--bg-surface);
            border-radius: 8px;
            padding: 12px;
            margin-top: 12px;
          }

          .result-group {
            margin-bottom: 8px;
          }

          .result-label {
            font-size: 11px;
            font-weight: 600;
            display: block;
            margin-bottom: 4px;
          }

          .result-label.pink { color: #E91E63; }
          .result-label.colorless { color: #999; }

          .compound-tags {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
          }

          .tag {
            font-size: 10px;
            padding: 2px 6px;
            border-radius: 3px;
          }

          .tag.pink {
            background: rgba(233, 30, 99, 0.2);
            color: #E91E63;
          }

          .tag.colorless {
            background: rgba(150, 150, 150, 0.2);
            color: #999;
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 550px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 12px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .results-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 11px;
          }

          .results-table th, .results-table td {
            padding: 6px 8px;
            text-align: left;
            border-bottom: 1px solid var(--border);
          }

          .results-table th {
            color: var(--text-secondary);
            font-weight: 600;
          }

          .results-table tr.basic {
            background: rgba(233, 30, 99, 0.1);
          }

          .results-table tr.basic td {
            color: #E91E63;
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp1"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
